/**
 * Dashboard table component showing cross-chain analysis
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { CrossPostMatch } from '../types/index';
import { StatusBadge, MatchScore } from './StatusBadge';
import { getSteemitPostUrl, getBlurtPostUrl } from '../utils/urlHelpers';

interface DashboardTableProps {
  data: CrossPostMatch[];
  isLoading: boolean;
  emptyMessage?: string;
  canMute: boolean;
  mutingAuthor: string | null;
  mutedAuthors: Set<string>;
  onMuteAuthor: (author: string) => void;
}

export function DashboardTable({
  data,
  isLoading,
  emptyMessage,
  canMute,
  mutingAuthor,
  mutedAuthors,
  onMuteAuthor,
}: DashboardTableProps) {
  const PAGE_SIZE = 50;
  const [currentPage, setCurrentPage] = useState(1);
  const topPaginationRef = useRef<HTMLDivElement | null>(null);

  const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return data.slice(start, start + PAGE_SIZE);
  }, [currentPage, data]);

  const rangeStart = data.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, data.length);

  const goToPage = (nextPage: number, scrollToTop: boolean) => {
    const clamped = Math.max(1, Math.min(totalPages, nextPage));
    if (clamped === currentPage) return;

    setCurrentPage(clamped);

    if (scrollToTop) {
      topPaginationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const renderPagination = (position: 'top' | 'bottom') => {
    const scrollToTop = position === 'bottom';

    return (
      <div
        ref={position === 'top' ? topPaginationRef : null}
        className={`px-6 py-4 bg-slate-50 text-sm text-slate-600 flex items-center justify-between gap-3 flex-wrap ${
          position === 'top'
            ? 'border-b border-slate-200'
            : 'border-t border-slate-200'
        }`}
      >
        <p>
          Showing {rangeStart}-{rangeEnd} of {data.length} results
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1, scrollToTop)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded border text-xs font-semibold ${
              currentPage === 1
                ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                : 'border-slate-300 text-slate-700 hover:bg-slate-100'
            }`}
          >
            Previous
          </button>
          <span className="text-xs font-semibold text-slate-700">
            Page {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => goToPage(currentPage + 1, scrollToTop)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded border text-xs font-semibold ${
              currentPage === totalPages
                ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                : 'border-slate-300 text-slate-700 hover:bg-slate-100'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hive"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-12 bg-slate-50 rounded-lg">
        <p className="text-slate-600">
          {emptyMessage || 'No data loaded. Click "Analyze" to fetch recent posts.'}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 shadow-md overflow-hidden">
      {data.length > PAGE_SIZE && renderPagination('top')}

      <div className="overflow-x-auto">
        <table className="w-full bg-white">
          <thead className="bg-slate-100 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Author
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                HIVE Post
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Steemit Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Blurt Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Match Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {pagedData.map((row, idx) => (
              <tr key={idx} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <a
                    href={`https://hive.blog/@${row.author}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-hive hover:underline"
                  >
                    @{row.author}
                  </a>
                </td>
                <td className="px-6 py-4">
                  <a
                    href={`https://hive.blog/@${row.author}/${row.hivePost.permlink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline truncate block max-w-xs"
                    title={row.hivePost.title}
                  >
                    {row.hivePost.title || row.hivePost.permlink}
                  </a>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-2">
                    <StatusBadge
                      active={row.steemitStatus.active}
                      last={row.steemitStatus.last}
                      error={row.steemitStatus.error}
                      chain="steemit"
                    />
                    {row.steemitContent && (
                      <a
                        href={getSteemitPostUrl(row.author, row.hivePost.permlink)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-green-600 hover:underline"
                      >
                        🔗 Cross-post found
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-2">
                    <StatusBadge
                      active={row.blurtStatus.active}
                      last={row.blurtStatus.last}
                      error={row.blurtStatus.error}
                      chain="blurt"
                    />
                    {row.blurtContent && (
                      <a
                        href={getBlurtPostUrl(row.author, row.hivePost.permlink)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        🔗 Cross-post found
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <MatchScore score={row.identityMatchScore} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {(() => {
                    const hasPositiveMatch = !!row.steemitContent || !!row.blurtContent;
                    const hasChainActivity = row.steemitStatus.active || row.blurtStatus.active;
                    if (!hasPositiveMatch && !hasChainActivity) {
                      return null;
                    }

                    const alreadyMuted = mutedAuthors.has(row.author.toLowerCase());
                    const isMuting = mutingAuthor === row.author;
                    const disabled = alreadyMuted || !canMute || isMuting;
                    const label = alreadyMuted ? 'Muted' : isMuting ? 'Muting...' : 'Mute';

                    return (
                      <button
                        type="button"
                        onClick={() => onMuteAuthor(row.author)}
                        disabled={disabled}
                        className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                          disabled
                            ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                            : 'bg-slate-800 text-white hover:bg-slate-700'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.length > PAGE_SIZE && renderPagination('bottom')}
    </div>
  );
}
